import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { getAccessToken } from '../services/authService';
import { markProgrammaticReload } from '../services/analytics/pageLoad';

export interface InactivityContextType {
  isInactive: boolean;
  isResuming: boolean;
  resetActivity: () => Promise<void>;
  /** Timestamp of the last user interaction, used to close analytics segments without trailing idle time. */
  getLastActivityAt: () => number;
}

const InactivityContext = createContext<InactivityContextType | undefined>(undefined);

export const useInactivity = (): InactivityContextType => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error('useInactivity must be used within an InactivityProvider');
  }
  return context;
};

/** Returns undefined instead of throwing, for consumers that must tolerate a missing provider. */
export const useOptionalInactivity = (): InactivityContextType | undefined => useContext(InactivityContext);

interface InactivityProviderProps {
  children: ReactNode;
  timeout?: number;
}

const DEFAULT_EVENTS = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
const DEFAULT_TIMEOUT = 15 * 60 * 1000; // 15 minutes

export const InactivityProvider: React.FC<InactivityProviderProps> = ({ children, timeout = DEFAULT_TIMEOUT }) => {
  const [isInactive, setIsInactive] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutValueRef = useRef(timeout);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    timeoutValueRef.current = timeout;
  }, [timeout]);

  const startInactivityTimer = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    lastActivityRef.current = Date.now();

    // Start new timeout
    timeoutRef.current = setTimeout(() => {
      setIsInactive(true);
    }, timeoutValueRef.current);
  }, []);

  const resetActivity = useCallback(async () => {
    if (isInactive) {
      try {
        setIsResuming(true);

        const token = await getAccessToken();

        // If token is null, it means acquireTokenRedirect was called
        // The page will redirect to Azure AD, so we keep showing the spinner
        // until the redirect completes (no need to update state)
        if (token === null) {
          // Redirect in progress - overlay stays visible until page navigates away
          return;
        }

        setIsInactive(false);
        setIsResuming(false);
        startInactivityTimer();
      } catch (error) {
        console.error('Failed to resume session:', error);
        // On token refresh failure, reload the page to force re-authentication
        markProgrammaticReload('auth_refresh_failed');
        window.location.reload();
      }
      return;
    }

    // If not inactive, just restart the timer
    startInactivityTimer();
  }, [isInactive, startInactivityTimer]);

  const handleActivity = useCallback(() => {
    // Only restart timer if we're not already inactive
    // This prevents constant timer resets when user is active
    if (!isInactive) {
      startInactivityTimer();
    }
  }, [isInactive, startInactivityTimer]);

  const getLastActivityAt = useCallback(() => lastActivityRef.current, []);

  useEffect(() => {
    // Add event listeners
    DEFAULT_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Start initial timer
    startInactivityTimer();

    // Cleanup function
    return () => {
      DEFAULT_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only run once on mount

  // Handle timeout changes for existing timer
  useEffect(() => {
    // If timeout changes and we're not inactive, restart the timer with new timeout
    if (!isInactive) {
      startInactivityTimer();
    }
  }, [timeout, isInactive, startInactivityTimer]);

  return (
    <InactivityContext.Provider value={{ isInactive, isResuming, resetActivity, getLastActivityAt }}>
      {children}
    </InactivityContext.Provider>
  );
};
